import axios from "axios";
import { message } from "antd";

import Decimal from "decimal.js";
import moment from "moment";

/**
 * @description Obtiene un nuevo folio para la Nota de Crédito
 * @returns folio 
 */
export const getNewFolioNC = async () => {
    try {
        let response = await axios.get("/nota-credito/new-folio")
        return response.data
    } catch (error) {
        message.error("Error al obtener el nuevo folio de Nota de Crédito.");
        return null;
    }
}


/**
 * @description Obtiene un nuevo folio según el tipo de documento
 * @param {string} tipo - Tipo de documento (por ejemplo, "NC" para Nota de Crédito)
 * @returns {Promise<string|null>} Folio del nuevo documento o null en caso de error
 */
export const getNewFolio = async (tipo) => {
    if (tipo === "NC") {
        return getNewFolioNC();
    }

    if(tipo === "PE") {
        // Lógica para obtener folio de comprobante de pago, si es diferente a la Nota de Crédito
        // Por ahora, retornamos un valor fijo ya que no se especificó la lógica para este caso
        return "0005";
    }
    return null;
}

/**
 * @description Obtiene una factura por su ID
 * @param {string} id - ID de la factura
 * @returns {Promise<Object|null>} Objeto de la factura o null en caso de error
 */
export const getFactura = async (id) => {
    try {

        let response = await axios.get('/facturas/get', {
            params: { 
                id,
            },
            headers: { Authorization: sessionStorage.getItem('token') }
        })
        return response?.data?.data
    
    } catch (error) {
        message.error("Error al obtener la factura. (services)");    
        return null;
    }
}

/**
 * @description Obtiene una orden por su ID
 * @param {string} id - ID de la orden
 * @returns {Promise<Object|null>} Objeto de la orden o null en caso de error
 */
export const getOrden = async (id) => {
    try{
        let response = await axios.get('/syncfy/ordenes_detalles', {
            params:{
                orden_id: id,
                cliente_id: true
            },
            headers: { Authorization: sessionStorage.getItem('token') }
        })
        return response?.data
            
    }catch(error) {
        message.error("Error al obtener la orden. (services)");
        return null;
    }
}

/**
 * @description Obtiene una transacción por su ID
 * @param {string} id - ID de la transacción
 * @returns {Promise<Object|null>} Objeto de la transacción o null en caso de error
 */
export const getTransaccion = async (id) => {
    try{
        let response = await axios.post('/transacciones/get', {
                id: id,
            },
            {
                headers: { Authorization: sessionStorage.getItem('token') }
            }
        )
        return response?.data?.data
            
    }catch(error) {
        message.error("Error al obtener la transacción. (services)");
        return null;
    }
}

/**
 * @memberof DrawerTimbrado
 * @method getConceptosFromFacturas
 * @description Obtiene los conceptos y totales de una factura y los agrega a los conceptos existentes
 * @param {Object|null} newFactura - Nueva factura a agregar o null si se eliminó una factura
 * @param {Array} facturas - Lista actual de facturas
 * @returns {Object} Objeto con conceptos_facturas, monto e imp_trasladados
 */
export const getConceptosFromFacturas = (newFactura, facturas = []) => {
    // Si newFactura es null, solo procesamos el array de facturas existente
    let facturasList = newFactura ? [...facturas, newFactura] : facturas
    let monto = 0
    let imp_trasladados = 0
    let conceptos_facturas = []

    // Si no hay facturas, retornamos valores vacíos
    if (facturasList.length === 0) {
        return {
            conceptos_facturas: [],
            monto: 0,
            imp_trasladados: 0
        }
    }


    let facturasConceptos = facturasList.filter(f => f?.conceptos?.length > 0)
    let facturasSinConceptos = facturasList.filter(f => !f?.conceptos || f.conceptos.length === 0)

    if(facturasConceptos.length > 0) {
        //Nota: las facturas que se hacen de forma individual (SPT, VMI), si guardan conceptos (12/Mar/26)
        // Si hay conceptos, los procesamos
        for (let factura of facturasConceptos) {
            if (factura?.conceptos?.length > 0) {
                for (let cons of factura.conceptos) {

                    let tieneImpuesto = cons.Impuestos?.Traslados && cons.Impuestos.Traslados.length > 0

                    conceptos_facturas.push({ ...cons, NoImpuesto: !tieneImpuesto })
                    monto = Decimal(monto).add(parseFloat(cons.Importe)).toDecimalPlaces(2).toNumber()
                    imp_trasladados = Decimal(imp_trasladados).add(parseFloat(cons.Impuestos?.Traslados[0]?.Importe ?? 0)).toDecimalPlaces(2).toNumber()
                }
            }
        }
    }

    //En caso de que haya facturas sin conceptos, las tenemos que obtner de del objeto request
    if (facturasSinConceptos.length > 0) {
        for (let factura of facturasSinConceptos) {
            if (factura?.request?.invoice_data?.Conceptos?.length > 0) {
                for (let cons of factura.request.invoice_data.Conceptos) {

                    let importe = cons.Importe
                    let impuesto_trasladado = cons.Impuestos?.Traslados[0]?.Importe ?? 0
                    let Total = Decimal(importe).add(impuesto_trasladado).toDecimalPlaces(2).toNumber()

                    conceptos_facturas.push({ ...cons, Total, NoImpuesto: impuesto_trasladado === 0 })
                    monto = Decimal(monto).add(parseFloat(cons.Importe)).toDecimalPlaces(2).toNumber()
                    imp_trasladados = Decimal(imp_trasladados).add(parseFloat(cons.Impuestos?.Traslados[0]?.Importe ?? 0)).toDecimalPlaces(2).toNumber()
                }
            }
        }
       
    } 

    return {
        conceptos_facturas,
        monto,
        imp_trasladados,
        total: Decimal(monto).add(imp_trasladados).toDecimalPlaces(2).toNumber()
    }
}

/**
 * @description Realiza el proceso de timbrado individual para una factura 
 * @param {*} data 
 * @returns 
 */
export const timbradoIndividual = async (data) => {
    try {
        let formattedData = formatData(data)

        let response = await axios.post('/syncfy/timbrado-v2/individual', formattedData, {
            headers: { Authorization: sessionStorage.getItem('token') }
        })
        return response?.data
    } catch (error) {
        throw error;
    }
}

/**
 * @description Antes de enviar los datos al timbrado, 
 * formateamos la información para que el proceso de timbrado pueda interpretarla correctamente
 * @param {*} data 
 */
const formatData = (data) => {
    let extractFields = [
        "cliente_id",
        "razon_social_id",
        "enviar_responsable",
        "enviar_receptor",
        "facturas_ids",
        "transaccion_id",
        "orden_id",
    ]

    let formattedData = {}
    let invoice_data = { ...data }
    
    // Extraemos los campos que no forman parte de la estructura de la factura y los eliminamos del objeto invoice_data
    for (let field of extractFields) {
        if (data[field] !== undefined) {
            formattedData[field] = data[field]
            delete invoice_data[field]
        }
    }
    invoice_data.Version = "4.0"

    //En caso de que tener impuestos trasladados, añadimos el nodo "Impuestos.Traslados[0]"
    if (invoice_data?.Impuestos?.TotalImpuestosTrasladados) {

        //sumamos el importe de los conceptos, solo si:
        // - NoImpuesto no está marcado como true
        // - ObjetoImp no es igual a "01"
        // - El campo "Impuestos.Traslados" existe y tiene al menos un elemento
        let impuestosTrasladosBase = invoice_data.Conceptos.reduce((acc, cons) => {
            let tieneImpuesto = cons.NoImpuesto !== true && cons.ObjetoImp !== "01" && cons.Impuestos?.Traslados && cons.Impuestos.Traslados.length > 0
            if (tieneImpuesto) {
                return Decimal(acc).add(parseFloat(cons.Importe)).toDecimalPlaces(2).toNumber()
            }
            return acc
        }, 0)

        invoice_data.Impuestos.Traslados = [
            {
                Base: impuestosTrasladosBase,
                Importe: invoice_data.Impuestos.TotalImpuestosTrasladados,
                Impuesto: "002",
                TasaOCuota: "0.160000",
                TipoFactor: "Tasa"
            }
        ]
    }

    if(invoice_data?.Impuestos?.TotalImpuestosTrasladados === 0) {
        delete invoice_data.Impuestos
    }

    if(invoice_data.Moneda == "MXN") delete invoice_data.TipoCambio
    if(invoice_data.Moneda == "USD" && !invoice_data.TipoCambio) throw new Error("El tipo de cambio es requerido cuando la moneda es USD")

    if(invoice_data.TipoDeComprobante === "P") {
        //confirmar campos
        invoice_data.Moneda = "XXX"
        delete invoice_data.TipoCambio

        let fechaPago = invoice_data?.Complemento?.Any?.[0]?.["Pago20:Pagos"]?.Pago?.[0]?.FechaPago
        invoice_data.Complemento.Any[0]["Pago20:Pagos"].Pago[0].FechaPago = moment(fechaPago).format("YYYY-MM-DDTHH:mm:ss")

        //recorremos los DoctoRelacionado para eliminar el nodo de "ImpuestosDR" si el campo "ObjetoImpDR" es === "01"
        let doctos = invoice_data?.Complemento?.Any?.[0]?.["Pago20:Pagos"]?.Pago?.[0]?.DoctoRelacionado || []
        doctos.forEach((dr, index) => {
            if (dr.ObjetoImpDR === "01") {
                delete invoice_data.Complemento.Any[0]["Pago20:Pagos"].Pago[0].DoctoRelacionado[index].ImpuestosDR
            }
        })

        //verificamos que si por lo menos un DoctoRelacionado tiene ObjetoImpDR diferente a "01", 
        //entonces el nodo de Totales tenga los campos de TotalTrasladosBaseIVA16 y TotalTrasladosImpuestoIVA16
        let tieneObjetoImpDR = doctos.some(dr => dr.ObjetoImpDR !== "01")
        if(!tieneObjetoImpDR){
            delete invoice_data.Complemento.Any[0]["Pago20:Pagos"].Totales.TotalTrasladosBaseIVA16
            delete invoice_data.Complemento.Any[0]["Pago20:Pagos"].Totales.TotalTrasladosImpuestoIVA16

            delete invoice_data.Complemento.Any[0]["Pago20:Pagos"].Pago[0].ImpuestosP
        }
        
    }

    formattedData.invoice_data = invoice_data

    //recorremos los conceptos para eliminar el nodo de "Impuestos" si el campo "NoImpuesto" está marcado como true,
    // u ObjetoImp es === "01" o si el campo "Impuestos.Traslados" no existe o está vacío 
    for (let i = 0; i < formattedData.invoice_data.Conceptos.length; i++) {
        let cons = formattedData.invoice_data.Conceptos[i]
        let noImpuesto = cons.NoImpuesto === true || cons.ObjetoImp === "01" || !cons.Impuestos?.Traslados || cons.Impuestos.Traslados.length === 0

        if (noImpuesto) {
            delete formattedData.invoice_data.Conceptos[i].Impuestos
        }
    }
    
    return formattedData
}

/**
 * @description Obtiene el tipo de cambio actual desde el backend, si hay un error se establece un valor por defecto de 18
 * @returns number - Tipo de cambio actual o valor por defecto
 */
export const getTipoCambioActual = async () => {
    try {
        let response = await axios.get("/tipo-cambio/get")
        return response?.data?.data[0]?.valor ?? 18
    } catch (error) {
        message.error("Error al obtener el tipo de cambio actual. Se establecerá un valor por defecto de 18.");
        return 18;
    }
}

export const cambiarEquivalenciaDR = (formRef, tipoCambio, MonedaP) => {
    const basePath = ["Complemento", "Any", 0, "Pago20:Pagos", "Pago", 0, "DoctoRelacionado"];

    //Recorremos los DoctoRelacionado para actualizar la equivalenciaDR dependiendo del tipo de cambio y la moneda del pago
    //EquivalenciaDR = unidades de MonedaDR por 1 unidad de MonedaP
    let DoctoRelacionado = formRef.getFieldValue(basePath) || []
    DoctoRelacionado.forEach((dr, index) => {
        if(dr.MonedaDR.toString() !== MonedaP.toString()){
            let equivalencia;
            if(MonedaP === "MXN" && dr.MonedaDR === "USD"){
                // 1 MXN = (1/tipoCambio) USD → EquivalenciaDR es un número pequeño
                equivalencia = new Decimal(1).div(new Decimal(tipoCambio)).toDecimalPlaces(10).toNumber();
            } else {
                // MonedaP=USD, MonedaDR=MXN → 1 USD = tipoCambio MXN
                equivalencia = tipoCambio;
            }
            formRef.setFieldValue([...basePath, index, "EquivalenciaDR"], equivalencia)
        }else{
            formRef.setFieldValue([...basePath, index, "EquivalenciaDR"], 1)
        }
    })
}

/**
 * @description Calcula los montos que se encuentran en el nodo 
 * "Totales" {
        "MontoTotalPagos": "116.0",
        "TotalTrasladosBaseIVA16": "99.9",
        "TotalTrasladosImpuestoIVA16": "15.9"
    }
 * @param {*} formRef 
 */
export const calcularTotalesComprobantePago = (formRef, BaseP, ImporteP) => {
    const TipoCambioP = new Decimal(formRef.getFieldValue(["Complemento", "Any", 0, "Pago20:Pagos", "Pago", 0, "TipoCambioP"]) || 1)
    const Monto = new Decimal(formRef.getFieldValue(["Complemento", "Any", 0, "Pago20:Pagos", "Pago", 0, "Monto"]) || 0)

    // Totales siempre en MXN: multiplicar por TipoCambioP para convertir de MonedaP a MXN
    // Cuando MonedaP=MXN, TipoCambioP=1, así que no cambia nada
    let MontoTotalPagos = Monto.mul(TipoCambioP).toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber()

    let TotalTrasladosBaseIVA16 = new Decimal(BaseP || 0).mul(TipoCambioP).toDecimalPlaces(2).toNumber()
    let TotalTrasladosImpuestoIVA16 = new Decimal(ImporteP || 0).mul(TipoCambioP).toDecimalPlaces(2).toNumber()

    let totales = { MontoTotalPagos }

    // Solo incluir nodos de impuestos si hay base/importe > 0
    if(TotalTrasladosBaseIVA16 > 0 || TotalTrasladosImpuestoIVA16 > 0) {
        totales.TotalTrasladosBaseIVA16 = TotalTrasladosBaseIVA16
        totales.TotalTrasladosImpuestoIVA16 = TotalTrasladosImpuestoIVA16
    }

    formRef.setFieldValue(["Complemento", "Any", 0, "Pago20:Pagos", "Totales"], totales)
}

export const calcularImpuestosPComplementoPago = (formRef) => {
    //BaseP e ImporteP se expresan en MonedaP (moneda del pago)
    //Para convertir de MonedaDR a MonedaP: valor / EquivalenciaDR
    let BaseP = new Decimal(0)
    let ImporteP = new Decimal(0)

    let DoctoRelacionado = formRef.getFieldValue(["Complemento", "Any", 0, "Pago20:Pagos", "Pago", 0, "DoctoRelacionado"]) || [];

    DoctoRelacionado.forEach(dr => {
        if (dr?.ObjetoImpDR !== "01") {
            let equivalenciaDR = new Decimal(dr.EquivalenciaDR || 1);
            let baseDR = new Decimal(dr.ImpuestosDR?.TrasladosDR?.[0]?.BaseDR || 0);
            let importeDR = new Decimal(dr.ImpuestosDR?.TrasladosDR?.[0]?.ImporteDR || 0);

            // Convertir de MonedaDR a MonedaP dividiendo entre EquivalenciaDR
            BaseP = BaseP.add(baseDR.div(equivalenciaDR));
            ImporteP = ImporteP.add(importeDR.div(equivalenciaDR));
        }
    })

    // BaseP hasta 6 decimales, ImporteP hasta 2 decimales (reglas SAT)
    BaseP = BaseP.toDecimalPlaces(6).toNumber()
    ImporteP = ImporteP.toDecimalPlaces(6).toNumber()

    formRef.setFieldValue(["Complemento", "Any", 0, "Pago20:Pagos", "Pago", 0, "ImpuestosP"], {
        TrasladosP: [
            {
                BaseP,
                ImporteP,
                ImpuestoP: "002",
                TasaOCuotaP: "0.160000",
                TipoFactorP: "Tasa"
            }
        ]
    })

    calcularTotalesComprobantePago(formRef, BaseP, ImporteP)
}